import { html } from 'htm/preact'
import { useEffect, useState } from 'preact/hooks'
import dayjs from 'dayjs'

export function SourcesSidebar({ sources, selectedSources: rawSelectedSources, onToggleSource, onToggleAllSources, articles }) {
  const selectedSources = rawSelectedSources || []
  const [sourcesWithCount, setSourcesWithCount] = useState([])
  const [expandedCategories, setExpandedCategories] = useState({
    'romanian-national': true,
    'romanian-regional': false,
    'romanian-institutional': false,
    'international': false,
    'technology': false,
    'business-finance': false,
    'specialized': false
  })
  
  useEffect(() => {
    if (sources && articles) {
      const now = dayjs()
      const last24h = now.subtract(24, 'hours')
      
      const countsMap = {}
      articles.forEach(article => {
        const articleTime = dayjs(article.published_at)
        if (articleTime.isAfter(last24h)) {
          countsMap[article.source_id] = (countsMap[article.source_id] || 0) + 1
        }
      })
      
      const updatedSources = sources.map(source => ({
        ...source,
        last24hCount: countsMap[source.id] || 0
      }))
      
      setSourcesWithCount(updatedSources)
    }
  }, [sources, articles])

  const categorizeSource = (source) => {
    const name = source.name.toLowerCase()
    
    // Romanian National News
    if (['hotnews', 'digi24', 'adevarul', 'news.ro', 'mediafax', 'agerpress', 'ziarul financiar', 'g4media', 'republica', 'europa libera romania', 'curs de guvernare', 'pro tv', 'profit.ro'].some(n => name.includes(n.toLowerCase()))) {
      return 'romanian-national'
    }
    
    // Romanian Institutional
    if (['dna', 'politia romana'].some(n => name.includes(n.toLowerCase()))) {
      return 'romanian-institutional'
    }
    
    // Romanian Regional (cities/counties)
    if (['cluj', 'timis', 'iasi', 'brasov', 'craiova', 'constanta', 'bihor', 'hunedoara', 'bacau', 'alba', 'buzau', 'mures', 'suceava', 'arad', 'sibiu'].some(n => name.includes(n.toLowerCase()))) {
      return 'romanian-regional'
    }
    
    // Technology
    if (['techcrunch', 'the verge', 'wired', 'mit technology'].some(n => name.includes(n.toLowerCase()))) {
      return 'technology'
    }
    
    // Business & Finance
    if (['bloomberg', 'wall street journal', 'cnbc', 'economedia', 'spot media', 'edupedu'].some(n => name.includes(n.toLowerCase()))) {
      return 'business-finance'
    }
    
    // International News
    if (['bbc', 'cnn', 'the guardian', 'deutsche welle', 'reuters', 'the atlantic'].some(n => name.includes(n.toLowerCase()))) {
      return 'international'
    }
    
    // Specialized
    return 'specialized'
  }

  const categories = {
    'romanian-national': {
      name: 'Știri Naționale România',
      icon: '🇷🇴',
      color: 'text-red-600 dark:text-red-400'
    },
    'romanian-regional': {
      name: 'Știri Regionale România', 
      icon: '🏘️',
      color: 'text-blue-600 dark:text-blue-400'
    },
    'romanian-institutional': {
      name: 'Instituții România',
      icon: '🏛️', 
      color: 'text-purple-600 dark:text-purple-400'
    },
    'international': {
      name: 'Știri Internaționale',
      icon: '🌍',
      color: 'text-green-600 dark:text-green-400'  
    },
    'technology': {
      name: 'Tehnologie',
      icon: '💻',
      color: 'text-indigo-600 dark:text-indigo-400'
    },
    'business-finance': {
      name: 'Business & Finanțe',
      icon: '💼',
      color: 'text-yellow-600 dark:text-yellow-400'
    },
    'specialized': {
      name: 'Specializate',
      icon: '📊',
      color: 'text-gray-600 dark:text-gray-400'
    }
  }

  const categorizedSources = sourcesWithCount.reduce((acc, source) => {
    const category = categorizeSource(source)
    if (!acc[category]) acc[category] = []
    acc[category].push(source)
    return acc
  }, {})

  // Sort sources within each category by name
  Object.keys(categorizedSources).forEach(category => {
    categorizedSources[category].sort((a, b) => a.name.localeCompare(b.name))
  })

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }))
  }

  const toggleCategorySelection = (categoryKey) => {
    const categorySources = categorizedSources[categoryKey] || []
    const activeSourcesInCategory = categorySources.filter(s => s.is_active)
    const selectedInCategory = activeSourcesInCategory.filter(s => selectedSources.includes(s.id))
    
    // If all are selected, deselect all; otherwise select all
    const shouldSelectAll = selectedInCategory.length < activeSourcesInCategory.length
    
    activeSourcesInCategory.forEach(source => {
      const isCurrentlySelected = selectedSources.includes(source.id)
      if (shouldSelectAll && !isCurrentlySelected) {
        onToggleSource(source.id)
      } else if (!shouldSelectAll && isCurrentlySelected) {
        onToggleSource(source.id)
      }
    })
  }

  const totalArticles = articles?.length || 0
  const last24hTotal = sourcesWithCount.reduce((sum, s) => sum + s.last24hCount, 0)
  const activeSources = sources.filter(s => s.is_active)
  const allActiveSelected = activeSources.length > 0 && activeSources.every(s => selectedSources.includes(s.id))
  const someSelected = selectedSources.length > 0
  const noneSelected = selectedSources.length === 0
  
  return html`
    <aside class="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full overflow-y-auto">
      <div class="p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold text-gray-900 dark:text-white">
            Surse de știri
          </h2>
          <button
            onClick=${onToggleAllSources}
            class="px-3 py-1 text-sm font-medium rounded-md transition-colors ${allActiveSelected 
              ? 'bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-300' 
              : 'bg-primary-100 hover:bg-primary-200 text-primary-700 dark:bg-primary-900/30 dark:hover:bg-primary-900/50 dark:text-primary-300'}"
          >
            ${allActiveSelected ? 'Deselectează toate' : 'Selectează toate'}
          </button>
        </div>
        
        <!-- Global Stats -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <input
                type="checkbox"
                checked=${allActiveSelected}
                ref=${(el) => {
                  if (el && someSelected && !allActiveSelected) {
                    el.indeterminate = true
                  }
                }}
                onChange=${onToggleAllSources}
                class="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
              />
              <div class="font-medium text-gray-900 dark:text-white">
                ${selectedSources.length} / ${activeSources.length} surse selectate
              </div>
            </div>
            <div class="text-right">
              <div class="text-2xl font-bold ${someSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-600'}">
                ${last24hTotal}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">ultimele 24h</div>
            </div>
          </div>
        </div>
        
        <!-- Categories -->
        <div class="space-y-3">
          ${Object.keys(categories).map(categoryKey => {
            const category = categories[categoryKey]
            const categorySourcesList = categorizedSources[categoryKey] || []
            const isExpanded = expandedCategories[categoryKey]
            const activeSourcesInCategory = categorySourcesList.filter(s => s.is_active)
            const selectedInCategory = activeSourcesInCategory.filter(s => selectedSources.includes(s.id))
            const categoryTotal24h = categorySourcesList.reduce((sum, s) => sum + s.last24hCount, 0)
            const categoryTotalArticles = categorySourcesList.reduce((sum, s) => sum + (s.article_count || 0), 0)
            const allCategorySelected = activeSourcesInCategory.length > 0 && selectedInCategory.length === activeSourcesInCategory.length
            const someCategorySelected = selectedInCategory.length > 0
            
            if (categorySourcesList.length === 0) return null
            
            return html`
              <div key=${categoryKey} class="border border-gray-200 dark:border-gray-600 rounded-lg">
                <!-- Category Header -->
                <div class="p-3">
                  <button
                    onClick=${() => toggleCategory(categoryKey)}
                    class="w-full flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 rounded p-1 transition-colors"
                  >
                    <div class="flex items-center gap-3">
                      <div class="flex items-center gap-2">
                        <span class="text-lg">${category.icon}</span>
                        <span class="font-medium text-gray-900 dark:text-white">
                          ${category.name}
                        </span>
                        <span class="text-sm text-gray-500 dark:text-gray-400">
                          (${categorySourcesList.length})
                        </span>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <div class="text-right mr-2">
                        <div class="text-lg font-bold ${category.color}">
                          ${categoryTotal24h}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                          ${categoryTotalArticles} total
                        </div>
                      </div>
                      <div class="transform transition-transform ${isExpanded ? 'rotate-90' : ''}">
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                        </svg>
                      </div>
                    </div>
                  </button>
                  
                  <div class="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked=${allCategorySelected}
                      ref=${(el) => {
                        if (el && someCategorySelected && !allCategorySelected) {
                          el.indeterminate = true
                        }
                      }}
                      onChange=${() => toggleCategorySelection(categoryKey)}
                      class="w-3 h-3 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-1"
                    />
                    <button
                      onClick=${() => toggleCategorySelection(categoryKey)}
                      class="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                    >
                      ${allCategorySelected ? 'Deselectează categoria' : 'Selectează categoria'}
                      ${someCategorySelected ? ` (${selectedInCategory.length}/${activeSourcesInCategory.length})` : ''}
                    </button>
                  </div>
                </div>

                <!-- Category Sources -->
                ${isExpanded ? html`
                  <div class="border-t border-gray-200 dark:border-gray-600">
                    ${categorySourcesList.map(source => {
                      const isSelected = selectedSources.includes(source.id)
                      
                      return html`
                        <button
                          key=${source.id}
                          onClick=${() => onToggleSource(source.id)}
                          class="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                        >
                          <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2 flex-grow">
                              <input
                                type="checkbox"
                                checked=${isSelected}
                                onClick=${(e) => e.stopPropagation()}
                                onChange=${() => onToggleSource(source.id)}
                                class="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                              />
                              <div class="flex-grow">
                                <div class="flex items-center gap-2">
                                  <div class="font-medium text-gray-900 dark:text-white text-sm">
                                    ${source.name}
                                  </div>
                                  ${!source.is_active ? html`
                                    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                      Inactiv
                                    </span>
                                  ` : null}
                                </div>
                                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  ${source.frequency_minutes ? `La fiecare ${source.frequency_minutes} min` : 'Manual'}
                                </div>
                              </div>
                            </div>
                            <div class="text-right">
                              <div class="text-lg font-bold ${source.last24hCount > 0 
                                ? category.color
                                : 'text-gray-400 dark:text-gray-600'}">
                                ${source.last24hCount}
                              </div>
                              <div class="text-xs text-gray-500 dark:text-gray-400">
                                ${source.article_count || 0} total
                              </div>
                            </div>
                          </div>
                        </button>
                      `
                    })}
                  </div>
                ` : null}
              </div>
            `
          })}
        </div>
        
        <!-- Overall Statistics -->
        <div class="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Statistici Generale
          </h3>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Total surse:</span>
              <span class="font-medium text-gray-900 dark:text-white">${sourcesWithCount.length}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Surse active:</span>
              <span class="font-medium text-gray-900 dark:text-white">
                ${sourcesWithCount.filter(s => s.is_active).length}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Total articole:</span>
              <span class="font-medium text-gray-900 dark:text-white">${totalArticles}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Articole noi (24h):</span>
              <span class="font-medium text-primary-600 dark:text-primary-400">
                ${last24hTotal}
              </span>
            </div>
          </div>
        </div>
      </div>
    </aside>
  `
}